import {
  BotVerificationSummary,
  ServerWebRules,
  TrafficRelatedResponse,
  TrafficSeriesResponse,
  TrafficTopResponse,
  WebRuleChangeResponse,
  type BotVerificationResult,
  type TrafficRelatedBy,
  type TrafficTopDimension,
  type WebRuleChangeRequest,
} from '@bartizo/shared';
import { apiPageRequest, apiRequest, type Page } from './client.js';

/** The Traffic page's reads (knowledge doc sections 8.7 and 15; P6.5). */

/** One server, one group's servers, or neither for the fleet. */
export interface TrafficScope {
  serverId?: string;
  groupId?: string;
  from?: string;
  to?: string;
}

const paramsOf = (scope: TrafficScope): URLSearchParams => {
  const params = new URLSearchParams();
  if (scope.serverId !== undefined) params.set('server_id', scope.serverId);
  if (scope.groupId !== undefined) params.set('group_id', scope.groupId);
  if (scope.from !== undefined) params.set('from', scope.from);
  if (scope.to !== undefined) params.set('to', scope.to);
  return params;
};

const withQuery = (path: string, params: URLSearchParams): string => {
  const query = params.toString();
  return query.length > 0 ? `${path}?${query}` : path;
};

export const getTrafficSeries = (scope: TrafficScope): Promise<TrafficSeriesResponse> =>
  apiRequest(withQuery('/traffic', paramsOf(scope)), { schema: TrafficSeriesResponse });

export function getTrafficTop(
  scope: TrafficScope,
  dimension: TrafficTopDimension,
  limit?: number,
  /** For a `site_*` dimension (P11.7): the site it is read for. */
  host?: string,
): Promise<TrafficTopResponse> {
  const params = paramsOf(scope);
  params.set('dimension', dimension);
  if (limit !== undefined) params.set('limit', String(limit));
  if (host !== undefined) params.set('host', host);
  return apiRequest(withQuery('/traffic/top', params), { schema: TrafficTopResponse });
}

/** What is joined to one row of a top list (P11.13): who asked for a page, what an address asked for. */
export function getTrafficRelated(
  scope: TrafficScope,
  by: TrafficRelatedBy,
  key: string,
  show: TrafficRelatedBy,
  limit = 8,
): Promise<TrafficRelatedResponse> {
  const params = paramsOf(scope);
  params.set('by', by);
  params.set('key', key);
  params.set('show', show);
  params.set('limit', String(limit));
  return apiRequest(withQuery('/traffic/related', params), { schema: TrafficRelatedResponse });
}

export interface VerificationFilters {
  serverId?: string;
  groupId?: string;
  result?: BotVerificationResult;
  limit?: number;
  cursor?: string;
}

export function listBotVerifications(
  filters: VerificationFilters,
): Promise<Page<BotVerificationSummary>> {
  const params = paramsOf(filters);
  if (filters.result !== undefined) params.set('result', filters.result);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.cursor !== undefined) params.set('cursor', filters.cursor);
  return apiPageRequest(withQuery('/bots/verifications', params), {
    schema: BotVerificationSummary,
  });
}

/** Which layer holds which bot rule for a server, and what it enforces (P6.6). */
export const getServerWebRules = (serverId: string): Promise<ServerWebRules> =>
  apiRequest(`/servers/${encodeURIComponent(serverId)}/web-rules`, { schema: ServerWebRules });

/** One change to one layer's bot rules: the Traffic page's Block, Rate-limit and Allow. */
export const changeWebRules = (body: WebRuleChangeRequest): Promise<WebRuleChangeResponse> =>
  apiRequest('/web-rules', { method: 'POST', body, schema: WebRuleChangeResponse });
